import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useUUID } from '~/Hooks';
import { ImageType } from '~/Models/DTO/Location.models';
import { requestUploadImage } from '~/store';

export interface IImageStyle {
  width?: number;
  height?: number;
}

export const useImageUploader = (
  images: ImageType[],
  onEditImage: (imageSrc: string, imageOrder: number) => void,
  onDragDrop?: (from: number, to: number) => void,
  onNewImage?: (imageSrc: string) => void,
  onDeleteImage?: (imageIndex: number) => void,
) => {
  const dispatch = useDispatch();
  const imageUploadAction = bindActionCreators(requestUploadImage, dispatch);
  const { createUUID } = useUUID();
  const [dragOrder, setDragOrder] = useState(0);
  const [editImageIndex, setEditImageIndex] = useState<number | undefined>();
  const [editImageSrc, setEditImageSrc] = useState<string>();
  const [isEditLoading, setEditLoading] = useState(false);

  const toDataURL = (url: string, callback: (data: string) => void) => {
    let xhr = new XMLHttpRequest();
    xhr.onload = () => {
      let reader = new FileReader();
      reader.onloadend = () => {
        callback(reader.result as string);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  };

  const handleDragStart = (event: React.DragEvent<HTMLImageElement>) => {
    const order = Number(event.currentTarget.dataset['order']);
    setDragOrder(order);
    const imgElement = document.getElementById(`image-${order}`);
    imgElement?.classList.add('dragging');
  };

  const handleImageEdit = async (imageDataUrl: string) => {
    setEditLoading(true);
    const imageResponse: any = await imageUploadAction(createUUID(), imageDataUrl);
    if (
      imageResponse?.url &&
      editImageIndex !== undefined &&
      images.length > 0 &&
      images[editImageIndex].order
    ) {
      onEditImage(imageResponse.url as string, images[editImageIndex].order as number);
    }
    setEditImageIndex(undefined);
    setEditLoading(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLImageElement>) => {
    event.preventDefault();
    const targetImage = images.find(
      (image) => image.order == Number(event.currentTarget.dataset['order']),
    );
    if (dragOrder !== undefined && targetImage?.order !== undefined && onDragDrop) {
      onDragDrop(dragOrder, targetImage?.order);
    }
  };

  const handleEditMode = (image: ImageType) => {
    setEditImageIndex(images.findIndex((img) => img.order === image.order));
  };

  const handleNewImage = (imageDataUrl: string) => {
    onNewImage?.(imageDataUrl);
  };

  const handleImageDelete = useCallback(() => {
    editImageIndex !== undefined && onDeleteImage?.(editImageIndex);
  }, [editImageIndex]);

  useEffect(() => {
    if (editImageIndex !== undefined && images.length > 0 && images[editImageIndex]) {
      toDataURL(images[editImageIndex].imageUrl, (data: any) => setEditImageSrc(data));
    }
  }, [editImageIndex]);

  return {
    setEditImageIndex,
    editImageSrc,
    editImageIndex,
    handleDragStart,
    handleImageEdit,
    isEditLoading,
    handleDragOver,
    handleDrop,
    handleImageDelete,
    handleNewImage,
    handleEditMode,
  };
};
